import { useEffect, useState } from 'react';
import {
  Plus, FileDown, FileSpreadsheet, Mail, ArrowRightCircle,
  Trash2, Pencil, Loader2, Copy, Star, StarOff, FileText,
} from 'lucide-react';
import api, { API_BASE } from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import QuoteEditorDialog from '@/pages/QuoteEditorDialog';

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso.slice(0, 10); }
};

function StatusChip({ status }) {
  const map = {
    draft: 'bg-neutral-100 text-neutral-700 border-neutral-200',
    sent: 'bg-[color:var(--ije-pink-tint)] text-[color:var(--ije-pink-dark)] border-[color:var(--ije-pink)]',
    converted: 'bg-green-50 text-green-700 border-green-200',
    expired: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  const cls = map[status] || map.draft;
  return (
    <span className={`sans text-[9px] tracking-widest uppercase px-1.5 py-0.5 border ${cls}`}>{status || 'draft'}</span>
  );
}

export default function AdminQuotesTab() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [view, setView] = useState('quotes'); // 'quotes' | 'templates'
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [templates, setTemplates] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/quotes-v2', {
        params: { templates: view === 'templates' ? 'yes' : 'no' },
      });
      setQuotes(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load quotes');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [view]);

  const loadTemplates = async () => {
    try {
      const { data } = await api.get('/admin/quotes-v2', { params: { templates: 'yes' } });
      setTemplates(data || []);
    } catch (e) {
      toast.error('Could not load templates');
    }
  };

  const openEditor = (id) => {
    setEditingId(id || null);
    setEditorOpen(true);
  };

  const openTemplatePicker = async () => {
    await loadTemplates();
    setTemplatePickerOpen(true);
  };

  const spawnFromTemplate = async (tpl) => {
    setBusyId(tpl.id);
    try {
      const { data } = await api.post(`/admin/quotes-v2/${tpl.id}/duplicate`, { as_template: false });
      toast.success(`New quote ${data.quote_number} from template`);
      setTemplatePickerOpen(false);
      // Immediately open the new quote in the editor so Priscila can tweak the client details
      setView('quotes');
      setEditingId(data.id);
      setEditorOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not use template');
    } finally { setBusyId(null); }
  };

  const duplicate = async (q) => {
    setBusyId(q.id);
    try {
      const { data } = await api.post(`/admin/quotes-v2/${q.id}/duplicate`, { as_template: false });
      toast.success(`Duplicated as ${data.quote_number}`);
      load();
      setEditingId(data.id);
      setEditorOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Duplicate failed');
    } finally { setBusyId(null); }
  };

  const toggleTemplate = async (q) => {
    setBusyId(q.id);
    try {
      if (!q.is_template) {
        const name = window.prompt('Template name (optional — defaults to the quote title):', q.title || '');
        if (name === null) { setBusyId(null); return; }
        await api.patch(`/admin/quotes-v2/${q.id}`, { is_template: true, template_name: name.trim() });
        toast.success('Saved as template');
      } else {
        await api.patch(`/admin/quotes-v2/${q.id}`, { is_template: false });
        toast.success('Template unmarked');
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setBusyId(null); }
  };

  const download = async (q, kind) => {
    setBusyId(q.id);
    try {
      const token = localStorage.getItem('ije_token');
      const res = await fetch(`${API_BASE}/admin/quotes-v2/${q.id}/${kind}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `ijehaus-${q.quote_number}.${kind === 'pdf' ? 'pdf' : 'xlsx'}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error('Download failed');
    } finally { setBusyId(null); }
  };

  const remove = async (q) => {
    if (!window.confirm(`Delete quote ${q.quote_number}? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/quotes-v2/${q.id}`);
      toast.success('Quote deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div data-testid="admin-quotes-tab">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {['quotes', 'templates'].map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={`sans text-[10px] tracking-widest uppercase px-3 py-1.5 border transition-colors ${view === v ? 'border-black bg-neutral-900 text-white' : 'border-neutral-200 text-neutral-500 hover:border-neutral-500'}`}
              data-testid={`admin-quotes-view-${v}`}
            >
              {v}
            </button>
          ))}
          <span className="sans text-xs text-neutral-500 ml-2">{quotes.length}</span>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <button type="button" onClick={openTemplatePicker} className="btn-outline h-10" data-testid="admin-quotes-from-template">
            <FileText size={14} /> From template
          </button>
          <button type="button" onClick={() => openEditor(null)} className="btn-primary h-10" data-testid="admin-quotes-new">
            <Plus size={14} /> New {view === 'templates' ? 'template' : 'quote'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin" /></div>
      ) : quotes.length === 0 ? (
        <div className="border border-dashed border-neutral-200 p-12 text-center sans text-sm text-neutral-500">
          {view === 'templates'
            ? <>No templates yet. On any quote tap <strong>⭐ Save as template</strong> to reuse it later.</>
            : <>No quotes yet. Tap <strong>New quote</strong> to build your first priced sheet.</>}
        </div>
      ) : (
        <>
          {/* Mobile */}
          <div className="md:hidden space-y-3">
            {quotes.map((q) => (
              <div key={q.id} className="border border-neutral-200 p-4" data-testid={`admin-quote-card-${q.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="serif text-base leading-tight truncate">{q.title || q.quote_number}</p>
                    <p className="sans text-[11px] text-neutral-500 mt-0.5 font-mono truncate">{q.quote_number}</p>
                    <p className="sans text-[11px] text-neutral-600 mt-1 truncate">
                      {q.client_company || q.client_name || <span className="text-neutral-400 italic">unassigned</span>}
                    </p>
                  </div>
                  <StatusChip status={q.status} />
                </div>
                <div className="flex items-baseline justify-between mt-3 pt-3 border-t border-neutral-100">
                  <span className="sans text-[10px] tracking-widest uppercase text-neutral-500">{fmtDate(q.created_at)}</span>
                  <span className="serif text-lg">{fmtMoney(q.total)}</span>
                </div>
                <div className="grid grid-cols-5 gap-1 mt-2">
                  <button type="button" onClick={() => openEditor(q.id)} className="flex flex-col items-center gap-1 py-2 text-neutral-700 active:bg-neutral-100 rounded-sm" data-testid={`admin-quote-edit-${q.id}`}>
                    <Pencil size={14} />
                    <span className="sans text-[9px] tracking-widest uppercase">Edit</span>
                  </button>
                  {view === 'templates' ? (
                    <button type="button" onClick={() => spawnFromTemplate(q)} disabled={busyId === q.id} className="flex flex-col items-center gap-1 py-2 text-[color:var(--ije-pink-dark)] active:bg-neutral-100 rounded-sm" data-testid={`admin-quote-use-template-${q.id}`}>
                      <Plus size={14} />
                      <span className="sans text-[9px] tracking-widest uppercase">Use</span>
                    </button>
                  ) : (
                    <button type="button" onClick={() => duplicate(q)} disabled={busyId === q.id} className="flex flex-col items-center gap-1 py-2 text-neutral-700 active:bg-neutral-100 rounded-sm" data-testid={`admin-quote-duplicate-${q.id}`}>
                      <Copy size={14} />
                      <span className="sans text-[9px] tracking-widest uppercase">Duplicate</span>
                    </button>
                  )}
                  <button type="button" onClick={() => toggleTemplate(q)} disabled={busyId === q.id} className="flex flex-col items-center gap-1 py-2 text-neutral-700 active:bg-neutral-100 rounded-sm" data-testid={`admin-quote-template-toggle-${q.id}`}>
                    {q.is_template ? <StarOff size={14} /> : <Star size={14} />}
                    <span className="sans text-[9px] tracking-widest uppercase">{q.is_template ? 'Unmark' : 'Template'}</span>
                  </button>
                  <button type="button" onClick={() => download(q, 'pdf')} disabled={busyId === q.id} className="flex flex-col items-center gap-1 py-2 text-neutral-700 active:bg-neutral-100 rounded-sm">
                    <FileDown size={14} />
                    <span className="sans text-[9px] tracking-widest uppercase">PDF</span>
                  </button>
                  <button type="button" onClick={() => remove(q)} className="flex flex-col items-center gap-1 py-2 text-neutral-500 active:bg-red-50 active:text-red-600 rounded-sm" data-testid={`admin-quote-delete-${q.id}`}>
                    <Trash2 size={14} />
                    <span className="sans text-[9px] tracking-widest uppercase">Delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop */}
          <table className="hidden md:table w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200">
                <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-left font-medium">Quote</th>
                <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-left font-medium">Client</th>
                <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-left font-medium">Status</th>
                <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-left font-medium">Created</th>
                <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-right font-medium">Total</th>
                <th className="sans text-xs uppercase tracking-widest text-neutral-500 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => (
                <tr key={q.id} className="border-b border-neutral-100" data-testid={`admin-quote-row-${q.id}`}>
                  <td className="py-3">
                    <p className="serif">{q.title || q.quote_number}</p>
                    <p className="sans text-[11px] text-neutral-500 font-mono">{q.quote_number}</p>
                  </td>
                  <td className="sans text-neutral-700">
                    {q.client_company || q.client_name
                      ? <>{q.client_company || q.client_name}{q.client_email && <div className="text-[11px] text-neutral-500">{q.client_email}</div>}</>
                      : <span className="text-neutral-400 italic">unassigned</span>}
                  </td>
                  <td><StatusChip status={q.status} /></td>
                  <td className="sans text-xs text-neutral-500">{fmtDate(q.created_at)}</td>
                  <td className="text-right serif">{fmtMoney(q.total)}</td>
                  <td className="text-right">
                    <div className="inline-flex items-center gap-1">
                      <button type="button" onClick={() => openEditor(q.id)} className="text-neutral-500 hover:text-black p-1.5" title="Edit" data-testid={`admin-quote-edit-desktop-${q.id}`}>
                        <Pencil size={15} />
                      </button>
                      {view === 'templates' ? (
                        <button type="button" onClick={() => spawnFromTemplate(q)} disabled={busyId === q.id} className="text-[color:var(--ije-pink-dark)] hover:text-black p-1.5" title="Use as new quote" data-testid={`admin-quote-use-template-desktop-${q.id}`}>
                          <Plus size={15} />
                        </button>
                      ) : (
                        <button type="button" onClick={() => duplicate(q)} disabled={busyId === q.id} className="text-neutral-500 hover:text-black p-1.5" title="Duplicate" data-testid={`admin-quote-duplicate-desktop-${q.id}`}>
                          <Copy size={15} />
                        </button>
                      )}
                      <button type="button" onClick={() => toggleTemplate(q)} disabled={busyId === q.id} className={`p-1.5 ${q.is_template ? 'text-[color:var(--ije-pink-dark)]' : 'text-neutral-500 hover:text-black'}`} title={q.is_template ? 'Unmark template' : 'Save as template'} data-testid={`admin-quote-template-toggle-desktop-${q.id}`}>
                        {q.is_template ? <StarOff size={15} /> : <Star size={15} />}
                      </button>
                      <button type="button" onClick={() => download(q, 'pdf')} disabled={busyId === q.id} className="text-neutral-500 hover:text-black p-1.5" title="Download PDF">
                        <FileDown size={15} />
                      </button>
                      <button type="button" onClick={() => download(q, 'xlsx')} disabled={busyId === q.id} className="text-neutral-500 hover:text-black p-1.5" title="Download Excel">
                        <FileSpreadsheet size={15} />
                      </button>
                      <button type="button" onClick={() => openEditor(q.id)} className="text-neutral-500 hover:text-black p-1.5" title="Email">
                        <Mail size={15} />
                      </button>
                      {q.status !== 'converted' && !q.is_template && (
                        <button type="button" onClick={() => openEditor(q.id)} className="text-neutral-500 hover:text-black p-1.5" title="Convert to order">
                          <ArrowRightCircle size={15} />
                        </button>
                      )}
                      <button type="button" onClick={() => remove(q)} className="text-neutral-500 hover:text-red-600 p-1.5" title="Delete">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <QuoteEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        quoteId={editingId}
        onSaved={load}
      />

      <TemplatePickerDialog
        open={templatePickerOpen}
        onOpenChange={setTemplatePickerOpen}
        templates={templates}
        onUse={spawnFromTemplate}
        busyId={busyId}
      />
    </div>
  );
}

function TemplatePickerDialog({ open, onOpenChange, templates, onUse, busyId }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-16px)] max-w-md p-4 sm:p-6 gap-3 !grid-cols-[minmax(0,1fr)]" data-testid="template-picker-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-lg">Start from a template</DialogTitle>
          <p className="sans text-[10px] tracking-widest uppercase text-neutral-500">Clones every line + custom price into a fresh draft quote</p>
        </DialogHeader>
        {templates.length === 0 ? (
          <div className="border border-dashed border-neutral-200 p-10 text-center sans text-sm text-neutral-500">
            No templates yet. On any quote tap <strong>⭐ Save as template</strong> to reuse it later.
          </div>
        ) : (
          <ul className="border border-neutral-200 max-h-80 overflow-y-auto divide-y divide-neutral-100">
            {templates.map((t) => {
              const n = (t.items || []).length;
              return (
                <li key={t.id} className="flex items-center gap-3 px-3 py-2.5">
                  <div className="flex-1 min-w-0">
                    <p className="serif text-sm truncate">{t.template_name || t.title || t.quote_number}</p>
                    <p className="sans text-[11px] text-neutral-500 mt-0.5">
                      {n} item{n === 1 ? '' : 's'} · ${Number(t.total || 0).toFixed(2)}
                    </p>
                  </div>
                  <button type="button" disabled={busyId === t.id} onClick={() => onUse(t)} className="btn-primary h-9 shrink-0 disabled:opacity-40" data-testid={`template-picker-use-${t.id}`}>
                    {busyId === t.id ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                    {busyId === t.id ? 'Loading' : 'Use'}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
